/**
 * User Behavior Tracking Context
 * Tracks all user actions (anonymous + logged-in) for analytics.
 * Uses a visitor_id stored in localStorage for anonymous continuity.
 */
import React, { createContext, useContext, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import { useAuth } from "./AuthContext";

const TrackingContext = createContext(null);

function getVisitorId() {
  let vid = localStorage.getItem("ik_visitor_id");
  if (!vid) {
    vid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("ik_visitor_id", vid);
  }
  return vid;
}

export function TrackingProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const visitorId = useRef(getVisitorId());
  const lastPage = useRef("");

  const trackEvent = useCallback((eventType, eventData = {}) => {
    const payload = {
      visitor_id: visitorId.current,
      user_id: user?.id || null,
      event_type: eventType,
      event_data: eventData,
      page_url: window.location.pathname,
      referrer: document.referrer,
    };
    // Fire and forget
    api.post("/api/track", payload).catch(() => {});
  }, [user]);

  const trackProductView = useCallback((productType, productId, productName) => {
    trackEvent("product_view", { product_type: productType, product_id: productId, name: productName });
    // Also record in recently_viewed
    api.post("/api/recently-viewed", {
      visitor_id: visitorId.current,
      user_id: user?.id || null,
      product_type: productType,
      product_id: productId,
    }).catch(() => {});
  }, [trackEvent, user]);

  // Auto-track page views on route change
  useEffect(() => {
    const page = location.pathname + location.search;
    if (page !== lastPage.current) {
      lastPage.current = page;
      trackEvent("page_view", { path: location.pathname, search: location.search });
    }
  }, [location, trackEvent]);

  return (
    <TrackingContext.Provider value={{ trackEvent, trackProductView, visitorId: visitorId.current }}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  return useContext(TrackingContext);
}
